// Tenantra Inc. - Auto-numbered unit list for multi-unit buildings, and suites for houses
import { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const MAX_UNITS = 500;
export const generateUnits = (count, start) => {
  const n = Math.min(Math.max(parseInt(count, 10) || 0, 0), MAX_UNITS);
  const s = parseInt(start, 10);
  if (!n || Number.isNaN(s)) return [];
  return Array.from({ length: n }, (_, i) => String(s + i));
};
export const rangeText = (units) => (units.length === 0 ? 'No units' : units.length === 1 ? `Unit ${units[0]}` : `${units.length} units, ${units[0]} to ${units[units.length - 1]}`);

const numField = 'h-10 text-sm bg-white border-slate-200';
const SUITES = ['Basement', 'Upper', 'Lower', 'Coach house'];

function Chips({ units, onRemove, fixed = [] }) {
  return (
    <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto p-0.5" data-testid="units-preview">
      {units.map(u => (
        <span key={u} className="group inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-700 tabular-nums" data-testid={`unit-chip-${u}`}>
          {u}
          {fixed.includes(u) ? <span className="w-4" /> : (
            <button type="button" onClick={() => onRemove(u)} aria-label={`Remove unit ${u}`} className="w-4 h-4 inline-flex items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`unit-remove-${u}`}><X className="w-3 h-3" /></button>
          )}
        </span>
      ))}
    </div>
  );
}

function AddOne({ units, onAdd, placeholder = 'Add a unit', testId = 'unit-add' }) {
  const [v, setV] = useState('');
  const [err, setErr] = useState('');
  const add = () => {
    const n = v.trim();
    if (!n) return;
    if (units.some(u => u.toLowerCase() === n.toLowerCase())) { setErr(`Unit ${n} is already in the list`); return; }
    if (units.length >= MAX_UNITS) { setErr(`A property can have at most ${MAX_UNITS} units`); return; }
    onAdd(n); setV(''); setErr('');
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input value={v} maxLength={20} onChange={(e) => { setV(e.target.value); setErr(''); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder={placeholder} className="h-8 text-sm w-40" data-testid={`${testId}-input`} />
        <button type="button" onClick={add} className="h-8 px-3 rounded-md border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`${testId}-btn`}><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
      {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
    </div>
  );
}

export function ApartmentUnits({ count, start, units, onChange }) {
  const regen = (c, s) => onChange({ count: c, start: s, units: generateUnits(c, s) });
  const tooMany = (parseInt(count, 10) || 0) > MAX_UNITS;
  return (
    <div className="space-y-4" data-testid="apartment-units">
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div>
          <Label htmlFor="unit-count" className="text-xs text-slate-600 mb-1 block">How many units?</Label>
          <Input id="unit-count" inputMode="numeric" value={count} onChange={(e) => regen(e.target.value.replace(/\D/g, '').slice(0, 4), start)} className={numField} placeholder="e.g. 10" data-testid="unit-count-input" />
        </div>
        <div>
          <Label htmlFor="unit-start" className="text-xs text-slate-600 mb-1 block">First unit number?</Label>
          <Input id="unit-start" inputMode="numeric" value={start} onChange={(e) => regen(count, e.target.value.replace(/\D/g, '').slice(0, 6))} className={numField} placeholder="e.g. 1 or 101" data-testid="unit-start-input" />
        </div>
      </div>
      {tooMany && <p className="text-xs text-red-600" data-testid="unit-count-error">A property can have at most {MAX_UNITS} units.</p>}
      {units.length > 0 && (
        <div className="border border-slate-200 rounded-lg bg-slate-50/60 p-3 space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-medium text-slate-800" data-testid="units-summary">{rangeText(units)}</p>
            <p className="text-[11px] text-slate-400">Remove any unit that doesn't exist, or add one that's numbered differently.</p>
          </div>
          <Chips units={units} onRemove={(u) => onChange({ count, start, units: units.filter(x => x !== u) })} />
          <AddOne units={units} onAdd={(n) => onChange({ count, start, units: [...units, n] })} />
        </div>
      )}
    </div>
  );
}

export function HouseUnits({ units, onChange }) {
  const suites = units.filter(u => u !== 'Main');
  return (
    <div className="space-y-3" data-testid="house-units">
      <p className="text-sm text-slate-600">The whole house is one rental. If part of it is rented separately, add it as a suite.</p>
      <Chips units={units} fixed={['Main']} onRemove={(u) => onChange(units.filter(x => x !== u))} />
      <div className="flex flex-wrap items-center gap-1.5">
        {SUITES.filter(s => !suites.includes(s)).map(s => (
          <button key={s} type="button" onClick={() => onChange([...units, s])} className="h-7 px-2.5 rounded-md border border-dashed border-slate-300 text-xs text-slate-600 hover:border-teal-400 hover:text-teal-700 inline-flex items-center gap-1" data-testid={`suite-add-${s.toLowerCase().replace(' ', '-')}`}><Plus className="w-3 h-3" /> {s}</button>
        ))}
      </div>
      <AddOne units={units} onAdd={(n) => onChange([...units, n])} placeholder="Other suite name" testId="suite-custom" />
    </div>
  );
}
